/**
 * `rewindTimeline` — the pure date maths behind the B3.3 scrubber. No React,
 * no network: it turns a `[from, now]` window into a day-indexed track the
 * scrubber slides over, and back again. Kept separate from the component so
 * the (testable) arithmetic never tangles with the (visual) handle.
 *
 * Issue #764. The scrubber's axis is **whole days** — the rewind is "the plan
 * as it stood on a day," not to-the-second. A position is an integer day
 * offset from `from` (0) to `totalDays` (= today). Each day maps to an
 * end-of-day instant so the as-of query returns everything that had occurred
 * by the close of that day (the natural "as it stood that day" semantic).
 *
 * Brand firewall (§A9): this is the PLAN's timeline. Dates describe when the
 * work tree changed, never a per-person activity axis.
 */

/** Milliseconds in one day. */
const DAY_MS = 24 * 60 * 60 * 1000;

/** Strip a date to local midnight (start of its day). */
function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

/** Whole-day difference `b - a` (calendar days, local). */
function dayDiff(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY_MS);
}

export interface RewindTimeline {
  /** Earliest selectable day (the track's left end). */
  readonly from: Date;
  /** Today (the track's right end — "now"). */
  readonly now: Date;
  /** Number of days the track spans (`now - from`); ≥ 0. */
  readonly totalDays: number;
}

/**
 * Build a day-indexed timeline from an earliest date up to now. `now`
 * defaults to the current instant (overridable for deterministic tests). The
 * window is clamped so `from ≤ now` and spans at least one day, so a brand-new
 * team (created today) still yields a usable, if degenerate, track.
 */
export function buildRewindTimeline(from: Date, now: Date = new Date()): RewindTimeline {
  const safeFrom = startOfDay(from <= now ? from : now);
  const today = startOfDay(now);
  const totalDays = Math.max(0, dayDiff(safeFrom, today));
  return { from: safeFrom, now: today, totalDays };
}

/** The Date at a given whole-day offset from `from` (clamped to the track). */
export function dayOffsetToDate(timeline: RewindTimeline, dayOffset: number): Date {
  const clamped = Math.min(Math.max(0, Math.round(dayOffset)), timeline.totalDays);
  return new Date(timeline.from.getTime() + clamped * DAY_MS);
}

/**
 * The ISO instant to query for a given day offset: the END of that day
 * (23:59:59.999 local) so "as of May 19" includes everything up to the close
 * of May 19. The "now" end uses the live `now` instant verbatim so the
 * rightmost position is exactly "the live tree."
 */
export function dayOffsetToQueryISO(
  timeline: RewindTimeline,
  dayOffset: number,
  now: Date,
): string {
  if (Math.round(dayOffset) >= timeline.totalDays) return now.toISOString();
  const day = dayOffsetToDate(timeline, dayOffset);
  day.setHours(23, 59, 59, 999);
  return day.toISOString();
}

/** True when the offset rests at the right end (= "now" / live). */
export function isAtNow(timeline: RewindTimeline, dayOffset: number): boolean {
  return Math.round(dayOffset) >= timeline.totalDays;
}

/** Compact, locale-friendly day label, e.g. "May 19". */
export function formatDayLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
